import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, LogOut, User, Users, Calendar, Home, ChevronDown, Settings, BookOpen, Award, BarChart, FileText } from 'lucide-react';
import MobileBottomNav from './MobileBottomNav';
import Footer from './Footer';
import SessionPlanningReport from './SessionPlanningReport';
import AttendanceReportModal from './AttendanceReportModal';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPlanningReport, setShowPlanningReport] = useState(false);
  const [showAttendanceReport, setShowAttendanceReport] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSubmenu = (menu: string) => {
    if (isSubmenuOpen === menu) {
      setIsSubmenuOpen(null);
    } else {
      setIsSubmenuOpen(menu);
    }
  };

  const mainNavItems = [
    { id: 'dashboard', path: '/', label: 'Dashboard', icon: <Home size={20} /> },
  ];

  const subMenus = {
    academy: [
      { path: '/players', label: 'Players', icon: <Users size={20} /> },
      { path: '/coaches', label: 'Coaches', icon: <User size={20} /> },
      { path: '/sessions', label: 'Sessions', icon: <Calendar size={20} /> },
    ],
    reports: [
      { id: 'planning', label: 'Session Planning Report', icon: <FileText size={20} />, onClick: () => { setShowPlanningReport(true); setIsSubmenuOpen(null); setIsMobileMenuOpen(false); } },
      { id: 'attendance', label: 'Attendance Report', icon: <BookOpen size={20} />, onClick: () => { setShowAttendanceReport(true); setIsSubmenuOpen(null); setIsMobileMenuOpen(false); } },
    ],
    settings: [
      { path: '/settings', label: 'Settings', icon: <Settings size={20} />, adminOnly: true },
    ]
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isSubMenuActive = (paths: { path: string }[]) => {
    return paths.some(item => location.pathname === item.path);
  };

  const handleMobileMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsSubmenuOpen(null);
    setIsMobileMenuOpen(false);
  };

  const canAccessSettings = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <nav className="bg-[#002855] text-white shadow-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* Left side: Logo & Brand */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img src="/mbfc-logo.svg" alt="MBFC Logo" className="h-10 w-10 mr-2" />
                <span className="text-xl font-bold hidden xl:inline">Academy Management System</span>
                <span className="text-xl font-bold hidden lg:inline xl:hidden">Academy Management</span>
                <span className="text-xl font-bold inline lg:hidden">AMS</span>
              </Link>
            </div>

            {/* Center: Main Navigation (Desktop) */}
            <div className="hidden lg:flex lg:items-center lg:space-x-2 xl:space-x-4 mx-4 flex-1 justify-center">
              {mainNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                    ? 'bg-[#001f44] text-white'
                    : 'text-blue-100 hover:bg-[#003c74]'
                    }`}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </button>
              ))}

              {/* Academy Dropdown */}
              <div className="relative">
                <button
                  onClick={() => toggleSubmenu('academy')}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isSubMenuActive(subMenus.academy) || isSubmenuOpen === 'academy'
                    ? 'bg-[#001f44] text-white'
                    : 'text-blue-100 hover:bg-[#003c74]'
                    }`}
                >
                  <span className="mr-2"><Users size={20} /></span>
                  Academy
                  <ChevronDown size={16} className="ml-1" />
                </button>
                {isSubmenuOpen === 'academy' && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40">
                    <div className="py-1">
                      {subMenus.academy.map((item) => (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isActive(item.path)
                            ? 'bg-gray-100 text-navy-800 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          <span className="mr-2">{item.icon}</span>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Reports Dropdown */}
              <div className="relative">
                <button
                  onClick={() => toggleSubmenu('reports')}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isSubmenuOpen === 'reports'
                    ? 'bg-[#001f44] text-white'
                    : 'text-blue-100 hover:bg-[#003c74]'
                    }`}
                >
                  <span className="mr-2"><BarChart size={20} /></span>
                  Reports
                  <ChevronDown size={16} className="ml-1" />
                </button>
                {isSubmenuOpen === 'reports' && (
                  <div className="absolute left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40">
                    <div className="py-1">
                      {subMenus.reports.map((item) => (
                        <button
                          key={item.id}
                          onClick={item.onClick}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <span className="mr-2">{item.icon}</span>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Settings (Admin/Superadmin Only) */}
              {canAccessSettings && (
                <button
                  onClick={() => handleNavigation('/settings')}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive('/settings')
                    ? 'bg-[#001f44] text-white'
                    : 'text-blue-100 hover:bg-[#003c74]'
                    }`}
                >
                  <Settings size={20} className="mr-2" />
                  Settings
                </button>
              )}
            </div>

            {/* Right side: User Profile & Logout (Desktop) */}
            <div className="hidden lg:flex items-center flex-shrink-0 space-x-4">
              <div className="flex items-center bg-[#001f44] px-3 py-1.5 rounded-full border border-[#003c74]">
                <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold mr-2">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <span className="text-sm font-medium text-white truncate max-w-[100px] xl:max-w-[150px]">
                  {user?.name}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center text-sm font-medium text-blue-100 hover:text-white transition-colors"
                title="Logout"
              >
                <LogOut size={18} className="mr-1" />
                <span className="hidden xl:inline">Logout</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center lg:hidden flex-shrink-0">
              <button
                onClick={handleMobileMenuClick}
                className="inline-flex items-center justify-center p-2 rounded-md text-blue-100 hover:text-white hover:bg-[#003c74] focus:outline-none"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">
                  {isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                </span>
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-40 bg-[#002855]">
            <div className="flex flex-col h-full">
              <div className="px-4 py-6 border-b border-[#003c74]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img src="/mbfc-logo.svg" alt="MBFC Logo" className="h-8 w-8 mr-2" />
                    <span className="text-lg font-bold text-white">MBFC Academy</span>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-white"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="flex-1 px-4 py-4 overflow-y-auto">
                <div className="space-y-4">
                  {mainNavItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={`flex items-center w-full px-3 py-2 rounded-md text-sm font-medium ${isActive(item.path)
                        ? 'bg-[#001f44] text-white'
                        : 'text-blue-100 hover:bg-[#003c74]'
                        }`}
                    >
                      <span className="mr-2">{item.icon}</span>
                      {item.label}
                    </button>
                  ))}

                  <div className="space-y-2">
                    <p className="px-3 text-xs font-semibold text-blue-200 uppercase tracking-wider">
                      Academy
                    </p>
                    {subMenus.academy.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={`flex items-center w-full px-3 py-2 rounded-md text-sm font-medium ${isActive(item.path)
                          ? 'bg-[#001f44] text-white'
                          : 'text-blue-100 hover:bg-[#003c74]'
                          }`}
                      >
                        <span className="mr-2">{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <p className="px-3 text-xs font-semibold text-blue-200 uppercase tracking-wider">
                      Reports
                    </p>
                    {subMenus.reports.map((item) => (
                      <button
                        key={item.id}
                        onClick={item.onClick}
                        className="flex items-center w-full px-3 py-2 rounded-md text-sm font-medium text-blue-100 hover:bg-[#003c74]"
                      >
                        <span className="mr-2">{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>

                  {canAccessSettings && (
                    <div className="space-y-2">
                      <p className="px-3 text-xs font-semibold text-blue-200 uppercase tracking-wider">
                        Settings
                      </p>
                      <button
                        onClick={() => handleNavigation('/settings')}
                        className={`flex items-center w-full px-3 py-2 rounded-md text-sm font-medium ${isActive('/settings')
                          ? 'bg-[#001f44] text-white'
                          : 'text-blue-100 hover:bg-[#003c74]'
                          }`}
                      >
                        <Settings size={20} className="mr-2" />
                        Settings
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 py-4 border-t border-[#003c74]">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-3 py-2 rounded-md text-sm font-medium text-blue-100 hover:bg-[#003c74]"
                >
                  <LogOut size={20} className="mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1 pb-16 md:pb-0">
        {children}
      </main>

      <Footer />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={handleMobileMenuClick} />

      {/* Report Modals */}
      {showPlanningReport && <SessionPlanningReport onClose={() => setShowPlanningReport(false)} />}
      {showAttendanceReport && <AttendanceReportModal onClose={() => setShowAttendanceReport(false)} />}
    </div>
  );
};

export default Layout;